import { useState, useCallback, useEffect, useMemo } from 'react';
import { projectService } from '../services/projectService';
import { matchDatePresetToRange, formatLocalYMD, getRangeFromPreset } from "@/utils/dateRangePresets";
import { PROJECT_PIPELINE_STAGES } from '../constants/projectConstants';
import { getActivePipelineIndex, resolveStatusForProjectStepper } from '../constants/projectPipelineStages';
import * as XLSX from 'xlsx';
import hotToast from 'react-hot-toast';

const formatIN = (n) => (n == null || Number.isNaN(Number(n)) ? 0 : Number(n)).toLocaleString('en-IN');

const formatDisplayDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const useProjects = () => {
  // UI State
  const [showDetails, setShowDetails] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // Polinaidu: 24-04-2026 (18:24) - Default to table (list) view
  const [editingProject, setEditingProject] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [quickViewId, setQuickViewId] = useState(null);
  const [managers, setManagers] = useState([]);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('venus_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const canAddProject = useMemo(() => {
    if (!user || !user.role) return false;
    return user.role === 'owner';
  }, [user]);

  // Data State
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalRevenue: 0,
    pipelineStats: {}
  });

  // Query & Pagination State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [datePreset, setDatePreset] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [customerName, setCustomerName] = useState('all');
  const [customerList, setCustomerList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // Status State
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);


  // DYNAMIC DATA FETCHING:
  // All project data displayed on the screen is fetched dynamically from the backend database via the API endpoint GET /verve-projects.
  // When filters, search queries, or page numbers change, this function triggers an API request to dynamically load the updated records.
  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { from, to } = dateRange;
      const params = {
        search: searchQuery,
        status: statusFilter,
        page
      };
      if (from) params.startDate = from;
      if (to) params.endDate = to;
      if (customerName && customerName !== 'all') {
        params.customerName = customerName;
      }

      const response = await projectService.getProjects(params);

      const formattedProjects = (response.projects || []).map((p) => {
        const totalReceived = p.totalReceived ?? p.advancePaid ?? 0;
        const balanceDue = p.netBalance ?? ((p.projectValue || 0) - totalReceived);
        const pm = p.projectManager;
        const pmLabel =
          pm && (typeof pm === 'object')
            ? (pm.name ||
              [pm.firstName, pm.lastName].filter(Boolean).join(' ').trim() ||
              'Not assigned')
            : 'Not assigned';
        const activeIdx = getActivePipelineIndex(p.projectStatus || p.status, p.installationMilestones || {}, {
          documentsStageComplete: !!p.documentsStageComplete,
          teamAssigned: (p.fieldOfficers?.length || 0) > 0,
          pmAssigned: !!p.projectManager
        });
        const pipelineStatus = PROJECT_PIPELINE_STAGES[activeIdx] || p.status;

        return {
          ...p,
          value: formatIN(p.projectValue),
          targetCostDisplay: formatIN(p.targetCost),
          advanceDisplay: formatIN(p.advancePaid || 0),
          // Polinaidu: 28-04-2026 (19:25) - Allowed negative balance for overpayments in projects table
          balance: formatIN(balanceDue),
          balancePaidOff: balanceDue <= 0.01,
          projectManager: pmLabel,
          pmPhone: p.projectManager?.phone || '',
          managerId: p.projectManager?._id,
          applicationDateDisplay: formatDisplayDate(p.applicationDate),
          startDateDisplay: formatDisplayDate(p.startDate),
          expectedEndDateDisplay: formatDisplayDate(p.expectedEndDate),
          createdAtDisplay: formatDisplayDate(p.createdAt),
          pipelineStatus,
          projectSummary: (() => {
            const t = p.structureType;
            const h = p.height;
            if (t && h != null && h !== '') return `${t} · ${h} m`;
            if (t) return t;
            if (h != null && h !== '') return `${h} m`;
            return '—';
          })()
        };
      });

      setProjects(formattedProjects);
      setTotalPages(response.totalPages || 1);
      setTotalItems(response.totalCount || 0);

      // Update customer list from backend response
      if (response.stats.customerList) {
        setCustomerList(response.stats.customerList);
      }

      // Format revenue stat for the dashboard
      setStats({
        ...response.stats,
        totalRevenue: response.stats.totalRevenue?.toLocaleString('en-IN') || '0',
        totalBalance: response.stats.totalBalance?.toLocaleString('en-IN') || '0',
        pipelineStats: response.stats.pipelineStats || {}
      });

      if (response.stats.statusList) {
        console.log("dynamically fetched statusList:", response.stats.statusList);
        setStatusList(response.stats.statusList);
      }

    } catch (err) {
      console.error('Fetch Projects Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, statusFilter, customerName, page, dateRange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProjects();
    }, 300); // Debounce search/filter
    return () => clearTimeout(timer);
  }, [fetchProjects]);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const data = await projectService.getProjectManagers();
        setManagers((data || []).filter((u) => u?.status !== 'Inactive'));
      } catch (err) {
        console.error('Failed to fetch managers:', err);
      }
    };
    fetchManagers();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleStartDateChange = (value) => {
    setDateRange((prev) => {
      const next = { ...prev, from: value };
      setDatePreset(matchDatePresetToRange(next.from, next.to));
      return next;
    });
    setPage(1);
  };

  const handleEndDateChange = (value) => {
    setDateRange((prev) => {
      const next = { ...prev, to: value };
      setDatePreset(matchDatePresetToRange(next.from, next.to));
      return next;
    });
    setPage(1);
  };

  const handleDatePreset = (preset) => {
    if (datePreset === preset || !preset || preset === 'all') {
      setDatePreset(null);
      setDateRange({ from: '', to: '' });
      setPage(1);
      return;
    }
    const range = getRangeFromPreset(preset);
    setDatePreset(preset);
    setDateRange(range);
    setPage(1);
  };

  const handleProjectSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (editingProject) {
        await projectService.updateProject(editingProject._id, data);
        setToast({ message: 'Project updated successfully!', type: 'success' });
      } else {
        // Polinaidu: 21-04-2026 (20:47) - Capture auto-generated projectId from response
        const res = await projectService.createProject(data);
        const pid = res?.projectId || '';
        setToast({
          message: `Project launched successfully! ${pid ? `(ID: ${pid})` : ''}`,
          type: 'success',
        });
      }
      setIsModalOpen(false);
      setEditingProject(null);
      fetchProjects();
    } catch (err) {
      setToast({ message: err.response?.data?.message || err.message, type: 'error' });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignManager = async (projectId, managerId) => {
    if (!managerId) return;
    setIsLoading(true);
    try {
      await projectService.updateProject(projectId, { projectManager: managerId });
      setToast({ message: 'Manager assigned!', type: 'success' });
      setAssigningId(null);
      fetchProjects();
    } catch (err) {
      setToast({ message: 'Failed to assign manager', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    setIsLoading(true);
    try {
      await projectService.deleteProject(id);
      setToast({ message: 'Project removed.', type: 'success' });
      fetchProjects();
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportProjects = async () => {
    const toastId = hotToast.loading('Preparing export...');
    try {
      const { from, to } = dateRange;
      const params = {
        search: searchQuery,
        status: statusFilter,
        isExport: 'true',
        limit: 10000,
        _t: Date.now()
      };
      if (from) params.startDate = from;
      if (to) params.endDate = to;
      if (customerName && customerName !== 'all') params.customerId = customerName;

      // Fetch both project list AND accounts overview (for expense breakdown)
      const [response, accountsData] = await Promise.all([
        projectService.getProjects(params),
        projectService.getAccountsOverview({ ...params, customerId: undefined, customerName: customerName !== 'all' ? customerName : undefined })
      ]);

      // Build a map of projectId -> expense breakdown from accounts overview
      const expenseMap = {};
      for (const acc of (accountsData.projects || [])) {
        expenseMap[String(acc._id)] = acc;
      }

      const rows = (response.projects || []).map(p => {
        const pm = p.projectManager;
        const pmName = pm && typeof pm === 'object' ? (pm.name || [pm.firstName, pm.lastName].filter(Boolean).join(' ').trim() || '') : '';
        const totalRecv = Number(p.totalReceived || p.advancePaid || 0);
        const pValue = Number(p.projectValue || 0);
        const netBal = Number(p.netBalance ?? (pValue - totalRecv));

        // Expense breakdown from accounts overview
        const acc = expenseMap[String(p._id)] || {};
        const materialExp = Number(acc.materialExpenses || 0);
        const standardAmt = Number(acc.standardAmount || 0);
        const nonStandardAmt = Number(acc.nonStandardAmount || 0);
        const othersAmt = Number(acc.othersAmount || 0);
        const epAmt = Number(acc.epAmount || 0);
        const miscExp = Number(acc.miscExpenses || 0);
        const totalExp = Number(acc.totalExpenses || 0);

        return {
          'Client Name': p.customerName || '',
          'Project ID': p.projectId || '',
          'Customer Project ID': p.applicationId || '',
          'SR Type': p.srType || '',
          'Site ID': p.siteId || '',
          'Site Name': p.siteName || '',
          'Customer': p.projectName || '',
          'Customer Site ID': p.customerSiteId || '',
          'Structure Type': p.structureType || '',
          'DIST': p.district || '',
          'Allocation Date': p.applicationDate ? new Date(p.applicationDate).toLocaleDateString('en-IN') : '',
          'Site Status': p.siteStatus || '',
          'Commencement Details': p.commencementDetails || '',
          'Remarks': p.remarks || '',
          'PO Number': p.poNumber || '',
          'PO Status': p.poStatus || '',
          'PO Value': pValue,
          'WCC Number': p.wccNumber || '',
          'WCC Status': p.wccStatus || '',
          'Invoice Number': p.invoiceNumber || '',
          'Invoice Date': p.invoiceDate ? new Date(p.invoiceDate).toLocaleDateString('en-IN') : '',
          'Invoice Status': p.invoiceStatus || '',
          'Project Manager': pmName,
          'Location': [p.address?.city, p.address?.state].filter(Boolean).join(', ') || '',
          'Progress': p.progress || 0,
          'Capacity': p.capacity || '',
          'Budget': Number(p.targetCost || 0),
          'Total Received': totalRecv,
          'Balance': netBal,
          // ─── Civil Expenses ───────────────────────────────────────────────
          'Civil Expense': Number(acc.civilExpense || 0),
          'Civil - Standard': Number(acc.civilStandard || 0),
          'Civil - Non Standard': Number(acc.civilNonStandard || 0),
          'Civil - Others': Number(acc.civilOthers || 0),
          // ─── Electrical Expenses ──────────────────────────────────────────
          'Electrical Expense': Number(acc.electricalExpense || 0),
          'Electrical - Standard': Number(acc.electricalStandard || 0),
          'Electrical - Non Standard': Number(acc.electricalNonStandard || 0),
          'Electrical - Others': Number(acc.electricalOthers || 0),
          // ─── General Expenses ──────────────────────────────────
          'Mechanical Expense': Number(acc.mechanicalExpense || 0),
          'Mechanical - Standard': Number(acc.mechanicalStandard || 0),
          'Mechanical - Non Standard': Number(acc.mechanicalNonStandard || 0),
          'Mechanical - Others': Number(acc.mechanicalOthers || 0),
          // ─── Overall Totals ───────────────────────────────────────────────
          'Material Expenses': Number(acc.materialExpenses || 0),
          'Standard Amount': Number(acc.standardAmount || 0),
          'Non Standard Amount': Number(acc.nonStandardAmount || 0),
          'Others Amount': Number(acc.othersAmount || 0),
          'Total Expenses': Number(acc.totalExpenses || 0),
          // ─────────────────────────────────────────────────────────────────
          'Start Date': p.startDate ? new Date(p.startDate).toLocaleDateString('en-IN') : '',
          'Expected End Date': p.expectedEndDate ? new Date(p.expectedEndDate).toLocaleDateString('en-IN') : '',
          'Created Date': p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN') : '',
        };
      });

      if (rows.length === 0) {
        hotToast.dismiss(toastId);
        hotToast.error('No projects to export');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = Object.keys(rows[0]).map(() => ({ wch: 20 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Projects');
      XLSX.writeFile(wb, `Verve_Projects_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
      hotToast.dismiss(toastId);
      hotToast.success(`Exported ${rows.length} projects!`);
    } catch (err) {
      console.error(err);
      hotToast.dismiss(toastId);
      hotToast.error('Failed to export projects');
    }
  };

  return {
    projects,
    fetchProjects,
    stats,
    isLoading,
    isModalOpen,
    setIsModalOpen,
    showDetails,
    setShowDetails,
    viewMode,
    setViewMode,
    searchQuery,
    handleSearch,
    statusFilter,
    handleStatusFilter,
    customerName,
    setCustomerName,
    customerList,
    statusList,
    dateRange,
    datePreset,
    handleStartDateChange,
    handleEndDateChange,
    handleDatePreset,
    handleProjectSubmit,
    handleDeleteProject,
    toast,
    setToast,
    page,
    setPage,
    totalPages,
    totalItems,
    editingProject,
    setEditingProject,
    assigningId,
    setAssigningId,
    handleAssignManager,
    user,
    canAddProject,
    quickViewId,
    setQuickViewId,
    managers,
    handleExportProjects
  };
};

// Polinaidu: 24-04-2026 (18:55) - Moved hardcoded logic (managers, permissions, quickview) from screen to hook.
